/* Prop-facing shapes for the Products UI components (designs/products-pdp.md
   PP1, PP4–PP7, PP9, PP18). Everything the shared contract already names is
   RE-EXPORTED from src/shared/product-pdp.ts — one definition, three call
   sites (the route, the store, the view). Only what a VIEW needs and the
   shared module does not carry lives here: the fixed chip words, the date
   shapes. */
import type { FeedRef } from "../../../../shared/feeds";
import type { GateDecision, SlotState } from "../../../../shared/product-pdp";

export type {
  CurrentEvidence,
  Evidence,
  FrozenPointer,
  GateDecision as Decision,
  PhaseBar,
  SlotCounts,
  SlotState,
} from "../../../../shared/product-pdp";
export { ESTIMATE_CAP_REFUSAL, ESTIMATE_MAX, UNCONFIRMED_WHY_REFUSAL } from "../../../../shared/product-pdp";

/* PP4 — what the evidence form hands its owner (the route stamps `recorded`);
   a clear is the drawer's own button, never a form arm */
export type EvidenceFormInput = Exclude<import("../../../../shared/product-pdp").EvidenceInput, { kind: "cleared" }>;
export type EvidenceInput = EvidenceFormInput;
/* the slot's CURRENT value, for prefilling the form and the Clear… button —
   the input shape; a stored CurrentEvidence is assignable (its `recorded`
   rides along unused) */
export type EvidenceValue = EvidenceFormInput;

/* PP7 decisions and their one label map — the raw enum never reaches the page */
export const DECISION_LABEL: Record<GateDecision, string> = {
  passed: "passed",
  "passed-with-conditions": "passed with conditions",
  held: "held",
};

/* the words the state chips carry (Deliverables table, review sheet, paper) */
export const STATE_LABEL: Record<SlotState, string> = {
  missing: "not recorded",
  estimated: "estimated",
  unconfirmed: "unconfirmed",
  evidence: "evidence",
};

/* the client-only refusal of the feed arm when the server lists no source */
export const NO_FEED_SOURCES = "No feed sources configured on this server";

/* PP7 / PP9 — the review input and the frozen certificate are the shared
   contract's now that the server records and freezes them (PR 4) */
export type { FrozenCertificate, FrozenCriterion, FrozenRow, GateReviewInput, PhaseCertificate } from "../../../../shared/product-pdp";

/* kept for the feed-kind prop shapes the components spell out */
export type { FeedRef };

/* PP18 date shapes: `Mon d, yyyy` for a YYYY-MM-DD or an ISO instant (UTC
   calendar day — a container's clock must not move a recorded date) */
export const fmtDate = (value: string): string =>
  new Date(value.length === 10 ? `${value}T00:00:00.000Z` : value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });

/* `Mon d, yyyy HH:MM` in the viewer's zone — the eyebrow's issue time and the
   "as of" line of the sheet (PR 4) */
export const fmtDateTime = (iso: string): string => {
  const d = new Date(iso);
  const day = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const hm = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${day} ${hm}`;
};

/* the state column's words: `est. {p}%` for an estimate, else the label */
export const stateText = (state: SlotState, percent?: number): string =>
  state === "estimated" && percent !== undefined ? `est. ${percent}%` : STATE_LABEL[state];
